"use client";

/**
 * `<CompassSynthVisual>` — animated brief card for the homepage hero.
 *
 * Cycles through 9 phases at `intervalMs` (default 700ms ≈ 6.3s loop).
 * Per dispatch open item: bump to 900ms if jitter shows up on lower-end
 * devices. The interval is configurable via prop for that exact reason.
 *
 * Phase 0: all dim. Phases 1..7: analyzer N-1 lights up + conviction
 * appears at phase 7. Phase 8: synthesis text reveals with shimmer.
 *
 * Ported from `marketing_v2/home.jsx > CompassSynthVisual`.
 */

import { useEffect, useState } from "react";
import { useVisibleAnimation } from "../hooks/use-visible-animation";

interface CompassSynthVisualProps {
  intervalMs?: number;
  weekOfLabel: string;
}

interface Analyzer {
  key: string;
  name: string;
  color: string;
  value: string;
}

const ANALYZERS: Analyzer[] = [
  { key: "FND", name: "Fundamental", color: "var(--color-blue)", value: "+12% rev YoY" },
  { key: "VAL", name: "Valuation", color: "var(--color-blue)", value: "FV $185-215" },
  { key: "SM", name: "Smart Money", color: "var(--color-gold)", value: "Insiders +$4.2M" },
  { key: "NRR", name: "Narrative", color: "var(--color-blue)", value: "Sentiment ↑ 8%" },
  { key: "MAC", name: "Macro", color: "var(--color-blue)", value: "Rates favorable" },
  { key: "TEC", name: "Technical", color: "var(--color-blue)", value: "50D > 200D" },
  { key: "CAT", name: "Catalyst", color: "var(--color-gold)", value: "Earnings · 8 days" },
];

export function CompassSynthVisual({ intervalMs = 700, weekOfLabel }: CompassSynthVisualProps) {
  const [phase, setPhase] = useState(0);
  const [containerRef, active] = useVisibleAnimation<HTMLDivElement>();

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setPhase((p) => (p + 1) % 9), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, active]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div
        style={{
          position: "relative",
          background:
            "linear-gradient(180deg, var(--color-surface) 0%, var(--color-surface-2) 100%)",
          border: "1px solid var(--color-line)",
          borderRadius: 16,
          padding: 28,
          boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.06),
            0 20px 60px -20px rgba(0,0,0,0.5),
            0 0 0 1px rgba(59,130,246,0.04)
          `,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 240,
            height: 240,
            background:
              "radial-gradient(circle, rgba(59,130,246,0.13), transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 22,
            position: "relative",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--color-green)",
                  animation: "wtm-pulse-dot 2s infinite",
                }}
              />
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--color-dim)",
                  letterSpacing: "0.14em",
                }}
              >
                COMPASS BRIEF · GENERATING
              </span>
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: "var(--color-text)",
                letterSpacing: "-0.015em",
              }}
            >
              NVDA · Nvidia Corp.
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-dim)",
                marginTop: 2,
              }}
            >
              Active monitoring · {weekOfLabel}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 10,
                color: "var(--color-dim)",
                letterSpacing: "0.14em",
                fontFamily: "var(--font-mono)",
              }}
            >
              CONVICTION
            </div>
            <div
              style={{
                fontSize: 34,
                fontWeight: 700,
                color: "var(--color-gold)",
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              {phase >= 7 ? (
                "8.4"
              ) : (
                <span
                  style={{
                    color: "var(--color-dim)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 18,
                  }}
                >
                  -.-
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--color-dim)",
                marginTop: 2,
              }}
            >
              / 10
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 6,
            marginBottom: 22,
          }}
        >
          {ANALYZERS.map((a, i) => {
            const active = phase > i;
            return (
              <div
                key={a.key}
                style={{
                  background: active
                    ? "rgba(59,130,246,0.10)"
                    : "rgba(255,255,255,0.02)",
                  border: `1px solid ${active ? "rgba(59,130,246,0.30)" : "var(--color-line)"}`,
                  borderRadius: 6,
                  padding: "8px 4px",
                  textAlign: "center",
                  transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9,
                    letterSpacing: "0.08em",
                    color: active ? a.color : "var(--color-dim)",
                    fontWeight: 600,
                  }}
                >
                  {a.key}
                </div>
                <div
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    margin: "4px auto 0",
                    background: active ? a.color : "var(--color-line)",
                    transition: "background 0.3s",
                  }}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            background: "var(--color-line)",
            borderRadius: 6,
            overflow: "hidden",
            border: "1px solid var(--color-line)",
          }}
        >
          {ANALYZERS.slice(0, 4).map((a, i) => {
            const active = phase > i;
            return (
              <div
                key={a.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 1fr auto",
                  gap: 12,
                  padding: "10px 14px",
                  background: "var(--color-surface)",
                  opacity: active ? 1 : 0.35,
                  transition: "opacity 0.4s",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--color-muted)",
                    fontFamily: "var(--font-mono)",
                    letterSpacing: "0.06em",
                  }}
                >
                  {a.name}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: active ? "var(--color-text)" : "var(--color-dim)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {a.value}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: active ? "var(--color-green)" : "var(--color-dim)",
                    fontFamily: "var(--font-mono)",
                    letterSpacing: "0.12em",
                  }}
                >
                  {active ? "✓ DONE" : "..."}
                </span>
              </div>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 18,
            padding: "16px 18px",
            background:
              phase >= 8
                ? "rgba(245,158,11,0.06)"
                : "rgba(255,255,255,0.02)",
            border: `1px solid ${phase >= 8 ? "rgba(245,158,11,0.25)" : "var(--color-line)"}`,
            borderRadius: 8,
            transition: "all 0.5s",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {phase < 8 ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
              }}
              aria-hidden="true"
            >
              <div
                style={{
                  width: "50%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, var(--color-line-2), transparent)",
                  animation: active
                    ? "wtm-shimmer 2s linear infinite"
                    : "none",
                  animationPlayState: active ? "running" : "paused",
                  willChange: "transform",
                }}
              />
            </div>
          ) : null}
          <div
            style={{
              fontSize: 10,
              color:
                phase >= 8 ? "var(--color-gold)" : "var(--color-dim)",
              letterSpacing: "0.14em",
              fontFamily: "var(--font-mono)",
              marginBottom: 6,
              fontWeight: 600,
            }}
          >
            SYNTHESIS
          </div>
          <div
            style={{
              fontSize: 13.5,
              color:
                phase >= 8 ? "var(--color-text)" : "var(--color-muted)",
              lineHeight: 1.55,
              fontWeight: 400,
            }}
          >
            {phase >= 8
              ? "Bullish setup intact. Earnings catalyst in 8 days; insider buying confirms management conviction. Watch item: the print itself."
              : "Combining signals from 7 analyzers..."}
          </div>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          top: -32,
          left: -32,
          width: "70%",
          height: 80,
          background: "var(--color-surface)",
          border: "1px solid var(--color-line)",
          borderRadius: 10,
          zIndex: -1,
          opacity: 0.4,
        }}
      />
    </div>
  );
}
